import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { Router } from '@angular/router';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { FuseConfigService } from '@fuse/services/config.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { NavigationItemInterface } from 'app/layout/components/navigation/models/navigation-item-interface';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthService } from 'app/core/services/auth.service';
import { DialogBoxService } from 'app/core/services/dialog-box/dialog-box.service';
import { FormStateService } from 'app/core/services/form-state.service';
import { NotificationService } from 'app/core/services/prothesis-api/notification.service';
import { WorkingLaboratoryService } from 'app/core/services/prothesis-api/working-laboratory.service';
import { WorkingCustomerService } from 'app/core/services/prothesis-customer-api/working-customer.service';
import { Language, SettingsService } from 'app/core/services/settings.service';
import { Area } from 'app/shared/enums/area.enum';
import find from 'lodash-es/find';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { locale as english } from './i18n/en';
import { locale as french } from './i18n/fr';
import { locale as portuguese } from './i18n/pt';
import { MatToolbar } from '@angular/material/toolbar';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { FuseShortcutsComponent } from '../../../../@fuse/components/shortcuts/shortcuts.component';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { AreaSelectorComponent } from './area-selector/area-selector.component';
import { ThemeSelectorComponent } from './theme-selector/theme-selector.component';
import { MatBadge } from '@angular/material/badge';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
  selector: 'app-toolbar',
  styleUrls: ['./toolbar.component.scss'],
  templateUrl: './toolbar.component.html',
  imports: [
    MatToolbar,

    MatIconButton,
    MatIcon,

    FuseShortcutsComponent,
    MatButton,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    AreaSelectorComponent,
    ThemeSelectorComponent,
    MatBadge,
    TranslatePipe,
  ],
})
export class ToolbarComponent implements OnInit, OnDestroy {
  public apiCredentialSettingsFlag: boolean;
  public applicationSettingsFlag: boolean;
  public customerAreaFlag: boolean | null;
  public customerAreaHelpLinkReleaseNote!: { displayFlag: boolean; icon: string; label: string; topSeparatorFlag: boolean; url?: string };
  public helpLinkContact = {
    displayFlag: true,
    icon: 'phone',
    label: 'Contacter Prothesis Dental Solutions',
    topSeparatorFlag: false,
    url: 'https://prothesis.cloud/contact-us',
  };
  public helpLinkDocs = {
    displayFlag: true,
    icon: 'menu_book',
    label: 'Documentation en ligne',
    topSeparatorFlag: true,
    url: 'https://prothesis.cloud/docs',
  };
  public helpLinkReleaseNote!: { displayFlag: boolean; icon: string; label: string; topSeparatorFlag: boolean; url?: string };
  public helpLinks: {
    action?: () => boolean;
    displayFlag: boolean;
    icon: string;
    isDisabledAction?: () => boolean;
    label: string;
    topSeparatorFlag: boolean;
    url?: string;
  }[];
  public helpLinkSupport = {
    displayFlag: true,
    icon: 'svg_support',
    label: 'Téléassistance PROTHESIS',
    topSeparatorFlag: false,
    url: 'https://prothesis.cloud/support/',
  };
  public helpLinkWarn = {
    displayFlag: true,
    icon: 'chat',
    label: 'Signaler un problème / donner un avis',
    topSeparatorFlag: true,
    url: 'https://prothesis.cloud/contact-us/#contact-form',
  };
  public hiddenNavbar: boolean;
  public horizontalNavbar: boolean;
  public laboratoryAreaFlag: boolean | null;
  public languages: Language[];
  public multiConnexionFlag = false;
  public navigation: NavigationItemInterface[] | undefined;
  public notificationNumber: number | undefined = undefined;
  public rightNavbar: boolean;
  public selectedLanguage: Language | undefined;
  public unreadNotificationNumber: number | undefined = undefined;
  public userManagementFlag: boolean;
  public username: string | null;

  private _authService = inject(AuthService);
  private _changeDetectorRef = inject(ChangeDetectorRef);
  private _dialogBoxService = inject(DialogBoxService);
  private _formStateService = inject(FormStateService);
  private _fuseConfigService = inject(FuseConfigService);
  private _fuseNavigationService = inject(FuseNavigationService);
  private _fuseSidebarService = inject(FuseSidebarService);
  private _fuseTranslationLoaderService = inject(FuseTranslationLoaderService);
  private _notificationService = inject(NotificationService);
  private _router = inject(Router);
  private _settingsService = inject(SettingsService);
  private _translateService = inject(TranslateService);
  private _unsubscribeAll$: Subject<void> = new Subject();
  private _workingCustomerService = inject(WorkingCustomerService);
  private _workingLaboratoryService = inject(WorkingLaboratoryService);

  public constructor() {
    const _settingsService = this._settingsService;

    this.languages = _settingsService.getSupportedLanguages();

    this._authService.isDoneLoading$.pipe(takeUntil(this._unsubscribeAll$)).subscribe((isDoneLoadingFlag) => {
      if (isDoneLoadingFlag) {
        if (this._authService.allowedCustomerUuids?.length > 0 && this._authService.allowedLaboratoryUuids?.length > 0) {
          this.multiConnexionFlag = true;
          this._changeDetectorRef.markForCheck();
        }
      }
    });

    this._authService.areaConfiguredFlag$.pipe(takeUntil(this._unsubscribeAll$)).subscribe((areaConfiguredFlag) => {
      if (areaConfiguredFlag) {
        this.helpLinks = this.buildHelpLinks(this._authService.area);
        this.setAreaFlag();
        this._changeDetectorRef.markForCheck();
      }
    });

    this.customerAreaHelpLinkReleaseNote = {
      displayFlag: true,
      icon: 'sticky_note_2',
      label: `Notes de version (version ${this._settingsService.version})`,
      topSeparatorFlag: false,
      url: 'https://prothesis.cloud/category/customer-area-release-notes/',
    };

    this.helpLinkReleaseNote = {
      displayFlag: true,
      icon: 'sticky_note_2',
      label: `Notes de version (version ${this._settingsService.version})`,
      topSeparatorFlag: false,
      url: 'https://prothesis.cloud/category/release-notes/',
    };

    this.navigation = [];
    this._fuseTranslationLoaderService.loadTranslations(french, english, portuguese);

    this.helpLinks = this.buildHelpLinks(this._authService.area);
    this.setAreaFlag();
  }

  public applicationSettings(): void {
    this._router.navigateByUrl('/application-settings');
  }

  public buildHelpLinks(area: Area | null | undefined): typeof this.helpLinks {
    if (this._authService.isAdminOrSuperAdminFlag) {
      return [this.helpLinkReleaseNote, this.helpLinkDocs, this.helpLinkWarn, this.helpLinkContact, this.helpLinkSupport];
    }
    if (area === Area.Customer) {
      return [this.customerAreaHelpLinkReleaseNote, this.helpLinkWarn];
    } else if (area === Area.Laboratory) {
      return [this.helpLinkReleaseNote, this.helpLinkDocs, this.helpLinkWarn, this.helpLinkContact, this.helpLinkSupport];
    } else {
      return [this.helpLinkWarn];
    }
  }

  public goToProfile(): void {
    this._router.navigateByUrl('');
  }

  public logout(): void {
    if (this._formStateService.initialValue && this._formStateService.hasChanged()) {
      this._dialogBoxService.promptForAction({
        messageLine1: "Vous n'avez pas enregistré vos modifications.",
        messageLine2: 'Quitter quand-même ce formulaire ?',
        onAccept: () => {
          this._workingCustomerService.clear();
          this._workingLaboratoryService.clear();
          this._authService.logout();
        },
      });
    } else {
      this._workingCustomerService.clear();
      this._workingLaboratoryService.clear();
      this._authService.logout();
    }
  }

  /**
   * Redirect to identity server account page
   */
  public myAccount(): void {
    this._router.navigateByUrl('/account');
  }

  public navigateToApiCredentials(): void {
    this._router.navigateByUrl('/api-credential');
  }

  public ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll$.next();
    this._unsubscribeAll$.complete();
  }

  public ngOnInit(): void {
    this._fuseNavigationService.onNavigationRegistered.pipe(takeUntil(this._unsubscribeAll$)).subscribe(() => {
      this.navigation = this._fuseNavigationService.getCurrentNavigation();
      this._changeDetectorRef.markForCheck();
    });

    // Subscribe to the config changes
    this._fuseConfigService.config.pipe(takeUntil(this._unsubscribeAll$)).subscribe((settings) => {
      this.horizontalNavbar = settings.layout.navbar.position === 'top';
      this.rightNavbar = settings.layout.navbar.position === 'right';
      this.hiddenNavbar = settings.layout.navbar.hidden === true;
      this._changeDetectorRef.markForCheck();
    });

    // Set the selected language from default languages
    this.selectedLanguage = find(this.languages, { id: this._translateService.currentLang() }) as Language;

    this._authService.isAuthenticated$
      .pipe(filter((isAuthenticate) => isAuthenticate))
      .pipe(takeUntil(this._unsubscribeAll$))
      .subscribe(() => {
        this.username = this._authService.name;
        this._changeDetectorRef.markForCheck();
      });

    this._authService.areaConfiguredFlag$.subscribe((areaConfiguredFlag: boolean) => {
      if (areaConfiguredFlag && this._workingLaboratoryService.laboratoryRole) {
        // Laboratory roles
        const laboratoryRole = this._workingLaboratoryService.laboratoryRole;
        this.applicationSettingsFlag =
          laboratoryRole.subscriptionSettingsFlag ||
          laboratoryRole.laboratorySettingsFlag ||
          laboratoryRole.storageSettingsFlag ||
          laboratoryRole.userCustomerSettingsFlag ||
          laboratoryRole.taxSettingsFlag ||
          laboratoryRole.fiscalYearSettingsFlag;
        this.apiCredentialSettingsFlag = laboratoryRole.apiCredentialSettingsFlag;
        this.userManagementFlag = laboratoryRole.laboratoryRoleSettingsFlag || laboratoryRole.userLaboratorySettingsFlag;
        this._changeDetectorRef.markForCheck();
      }
    });

    this._workingLaboratoryService.exploredUuid$
      .pipe(takeUntil(this._unsubscribeAll$))
      .pipe(distinctUntilChanged())
      .pipe(filter((laboratoryUuid) => !laboratoryUuid))
      .subscribe(() => {
        this.notificationNumber = 0;
        this.unreadNotificationNumber = 0;
        this._changeDetectorRef.markForCheck();
      });

    this._notificationService.onNotificationRefreshed$.pipe(takeUntil(this._unsubscribeAll$)).subscribe((notifications) => {
      this.notificationNumber = notifications?.length;
      this.unreadNotificationNumber = notifications?.filter((x) => NotificationService.unreadNotificationFlag(x))?.length;
      this._changeDetectorRef.markForCheck();
    });
  }

  public setAreaFlag(): void {
    this.laboratoryAreaFlag = this._authService.area === Area.Laboratory || this._workingLaboratoryService.exploringFlag;
    this.customerAreaFlag = this._authService.area === Area.Customer || this._workingCustomerService.exploringFlag;
  }

  public setLanguage(lang: Language): void {
    // Set the selected language for the toolbar
    this.selectedLanguage = lang;

    // Use the selected language for translations
    this._translateService.use(lang.id);

    this._settingsService.setLanguage(lang.id);

    window.location.reload();
  }

  public toggleSidebarOpen(key: string): void {
    this._fuseSidebarService.getSidebar(key)!.toggleOpen();
  }

  public userManagement(): void {
    this._router.navigateByUrl('/user-management');
  }
}
